import React, { useEffect, useState } from 'react';
import { Check, ChevronRight, Star, UsersRound, UserCheck, RotateCcw, Calendar, Clock, FileText, MessageSquare } from 'lucide-react';
import { message } from 'antd';
import apiClient from '../../utils/apiClient';
import VietnameseDatePicker from '../../components/documents/VietnameseDatePicker';
import CustomSelect from '../../components/base/CustomSelect';
import './ReceptionKiosk.css';

const ratingLabels = { 
  1: 'Rất chưa hài lòng', 
  2: 'Chưa hài lòng', 
  3: 'Bình thường', 
  4: 'Hài lòng', 
  5: 'Rất hài lòng' 
};

export const COUNTER_OPTIONS = [
  { value: 'QUAY_1', label: 'Quầy 1' },
  { value: 'QUAY_2', label: 'Quầy 2' },
  { value: 'QUAY_3', label: 'Quầy 3' },
  { value: 'QUAY_4', label: 'Quầy 4' },
  { value: 'QUAY_5', label: 'Quầy 5' },
  { value: 'QUAY_6', label: 'Quầy 6' },
  { value: 'QUAY_7', label: 'Quầy 7' },
  { value: 'QUAY_8', label: 'Quầy 8' },
];

export const TIME_SLOT_OPTIONS = [
  '07:30 - 08:30',
  '08:30 - 09:30',
  '09:30 - 10:30',
  '10:30 - 11:30',
  '13:30 - 14:30',
  '14:30 - 15:30',
  '15:30 - 16:30',
  '16:30 - 17:00'
];

function RatingStars({ value, onChange }) {
  return (
    <div className="reception-kiosk-stars" role="radiogroup" aria-label="Đánh giá số sao">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={star <= value ? 'is-active' : ''}
          aria-label={`${star} trên 5 sao`}
          aria-pressed={star <= value}
          onClick={() => onChange(star)}
        >
          <Star size={56} fill={star <= value ? 'currentColor' : 'none'} strokeWidth={1.8} />
        </button>
      ))}
    </div>
  );
}

export default function ReceptionKiosk() {
  // Step 1: Officer setup | Step 2: Citizen Star Rating | Step 3: Submitted Thank You
  const [step, setStep] = useState(1);
  
  // Officer Input Form State - Khớp 100% các trường của API
  const [formData, setFormData] = useState({
    receptionCode: '',
    citizenName: '',
    officerName: '',
    counterCode: 'QUAY_1',
    receptionDate: new Date().toISOString().slice(0, 10),
    timeSlot: '08:30 - 09:30',
    workingContent: ''
  });
  const [fieldErrors, setFieldErrors] = useState({
    receptionCode: '',
    officerName: '',
    citizenName: '',
    workingContent: ''
  });
  const [touched, setTouched] = useState({});

  // Citizen Rating State
  const [overall, setOverall] = useState(0);
  const [citizenComment, setCitizenComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Real-time Field Validator
  const validateField = (name, value) => {
    let error = '';
    const v = String(value || '').trim();
    if (name === 'receptionCode') {
      if (!v) error = 'Vui lòng nhập Mã tiếp dân';
      else if (v.length < 4) error = 'Mã tiếp dân phải có ít nhất 4 ký tự';
      else if (v.length > 50) error = 'Mã tiếp dân không được vượt quá 50 ký tự';
      else if (!/^[A-Z0-9-]+$/i.test(v)) error = 'Mã tiếp dân chỉ được chứa chữ cái, số và dấu gạch nối (-)';
    } else if (name === 'officerName') {
      if (!v) error = 'Vui lòng nhập Tên cán bộ tiếp nhận';
      else if (v.length < 2) error = 'Tên cán bộ phải có ít nhất 2 ký tự';
      else if (v.length > 100) error = 'Tên cán bộ không được vượt quá 100 ký tự';
    } else if (name === 'citizenName') {
      if (!v) error = 'Vui lòng nhập Họ tên công dân';
      else if (v.length < 2) error = 'Họ tên công dân phải có ít nhất 2 ký tự';
      else if (v.length > 100) error = 'Họ tên công dân không được vượt quá 100 ký tự';
    } else if (name === 'workingContent') {
      if (!v) error = 'Vui lòng nhập Nội dung tiếp dân / làm việc';
      else if (v.length < 5) error = 'Nội dung làm việc phải có ít nhất 5 ký tự';
      else if (v.length > 2000) error = 'Nội dung làm việc không được vượt quá 2000 ký tự';
    }
    setFieldErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleFieldBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  // Initialize initial default form
  useEffect(() => {
    resetToNewSession();
  }, []);

  const resetToNewSession = () => {
    const now = new Date();
    const datePart = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const rand = Math.floor(100 + Math.random() * 900);
    const currentDate = now.toISOString().slice(0, 10);

    // Tính slot giờ gần nhất
    const currentHour = now.getHours();
    let defaultSlot = '08:30 - 09:30';
    if (currentHour < 9) defaultSlot = '08:30 - 09:30';
    else if (currentHour < 10) defaultSlot = '09:30 - 10:30';
    else if (currentHour < 11) defaultSlot = '10:30 - 11:30';
    else if (currentHour < 14) defaultSlot = '13:30 - 14:30';
    else if (currentHour < 15) defaultSlot = '14:30 - 15:30';
    else if (currentHour < 16) defaultSlot = '15:30 - 16:30';
    else defaultSlot = '16:30 - 17:00';

    setFormData(prev => ({
      receptionCode: `TD-${datePart}-${rand}`,
      citizenName: '',
      officerName: prev.officerName || '',
      counterCode: prev.counterCode || 'QUAY_1',
      receptionDate: currentDate,
      timeSlot: defaultSlot,
      workingContent: ''
    }));
    setFieldErrors({
      receptionCode: '',
      officerName: '',
      citizenName: '',
      workingContent: ''
    });
    setTouched({});
    setOverall(0);
    setCitizenComment('');
    setSubmitted(false);
    setStep(1);
  };

  // Auto-reset back to Officer setup after 4s on thank you screen
  useEffect(() => {
    if (!submitted) return undefined;
    const timer = window.setTimeout(() => {
      resetToNewSession();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [submitted]);

  // Officer clicks "Chuyển sang đánh giá"
  const handleStartCitizenRating = (e) => {
    e.preventDefault();
    const errCode = validateField('receptionCode', formData.receptionCode);
    const errOfficer = validateField('officerName', formData.officerName);
    const errCitizen = validateField('citizenName', formData.citizenName);
    const errContent = validateField('workingContent', formData.workingContent);
    setTouched({
      receptionCode: true,
      officerName: true,
      citizenName: true,
      workingContent: true
    });

    if (errCode || errOfficer || errCitizen || errContent) {
      return;
    }

    setOverall(0);
    setCitizenComment('');
    setStep(2); // Chuyển sang màn hình người dân
  };

  // Citizen submits Rating
  const handleSendRating = async () => {
    if (!overall || overall === 0) {
      message.warning('Vui lòng chọn số sao đánh giá');
      return;
    }

    setSubmitting(true);
    try {
      // Gói tin chính xác 100% theo đúng 9 trường của Swagger API
      const payload = {
        receptionCode: formData.receptionCode.trim().toUpperCase(),
        citizenName: formData.citizenName.trim(),
        officerName: formData.officerName.trim(),
        counterCode: formData.counterCode,
        receptionDate: formData.receptionDate,
        timeSlot: formData.timeSlot,
        workingContent: formData.workingContent.trim(),
        score: Number(overall),
        comment: citizenComment.trim() || 'Hài lòng với chất lượng phục vụ của cán bộ.'
      };

      const res = await apiClient.post('/api/reception-ratings', payload);
      if (res.data?.success) {
        message.success('Đã gửi đánh giá thành công!');
      }
      setSubmitted(true);
    } catch (error) {
      console.error("Feedback API save error:", error);
      const errMsg = error.response?.data?.message || error.response?.data?.errors?.[0]?.message || 'Có lỗi khi gửi đánh giá';
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="reception-kiosk reception-kiosk-success">
        <div className="reception-kiosk-success-card animate-fade-in">
          <div className="reception-kiosk-success-icon">
            <Check size={46} strokeWidth={3} />
          </div>
          <p className="reception-kiosk-eyebrow">Đã ghi nhận thành công</p>
          <h1>Cảm ơn bạn đã đánh giá!</h1>
          <p>Ý kiến đánh giá của bạn giúp UBND Phường không ngừng nâng cao chất lượng phục vụ.</p>
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={resetToNewSession}
              className="reception-kiosk-back"
              style={{ fontSize: '14px', color: '#1565c0', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <RotateCcw size={16} /> Tạo lượt tiếp dân tiếp theo
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="reception-kiosk">
      {/* Header */}
      <header className="reception-kiosk-header">
        <div className="reception-kiosk-brand">
          <span className="reception-kiosk-mark">
            <UsersRound size={24} />
          </span>
          <span>
            <strong>SOS TNP</strong>
            <small>Đánh giá tiếp công dân</small>
          </span>
        </div>

        {step === 2 && (
          <button 
            type="button" 
            className="reception-kiosk-back" 
            onClick={() => setStep(1)}
            title="Quay lại cài đặt cán bộ"
          >
            ← Cán bộ chỉnh sửa thông tin
          </button>
        )}
      </header>

      {/* Main Content Area */}
      <section className="reception-kiosk-content">
        
        {/* BƯỚC 1: CÁN BỘ NHẬP THÔNG TIN CA TIẾP DÂN */}
        {step === 1 && (
          <div className="reception-kiosk-welcome animate-fade-in">
            <div className="reception-kiosk-hero-icon" style={{ backgroundColor: '#e0f2fe', color: '#0284c7' }}>
              <UserCheck size={42} />
            </div>
            <p className="reception-kiosk-eyebrow">Dành cho cán bộ tiếp dân</p>
            <h1 style={{ fontSize: '30px', marginBottom: '6px' }}>Thông tin ca tiếp dân</h1>
            <p className="reception-kiosk-lead" style={{ maxWidth: '520px', margin: '0 auto 24px', fontSize: '14px' }}>
              Vui lòng nhập đầy đủ thông tin ca tiếp dân trước khi đưa iPad cho người dân đánh giá.
            </p>

            <form onSubmit={handleStartCitizenRating} noValidate style={{ maxWidth: '560px', margin: '0 auto', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* 1. Mã tiếp dân (receptionCode) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                    Mã tiếp dân * <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>(4 - 50 ký tự)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={50}
                    value={formData.receptionCode}
                    onChange={(e) => handleFieldChange('receptionCode', e.target.value.toUpperCase())}
                    onBlur={() => handleFieldBlur('receptionCode')}
                    placeholder="VD: TD-20260823-001"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      fontSize: '14px',
                      fontWeight: '700',
                      borderRadius: '10px',
                      border: touched.receptionCode && fieldErrors.receptionCode ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                      outline: 'none',
                      textTransform: 'uppercase',
                      backgroundColor: touched.receptionCode && fieldErrors.receptionCode ? '#fef2f2' : '#fff'
                    }}
                  />
                  {touched.receptionCode && fieldErrors.receptionCode && (
                    <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', margin: '4px 0 0', fontWeight: '600' }}>
                      ⚠️ {fieldErrors.receptionCode}
                    </p>
                  )}
                </div>

                {/* 2. Quầy tiếp nhận (counterCode) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                    Quầy phụ trách *
                  </label>
                  <CustomSelect
                    value={formData.counterCode}
                    onChange={(val) => handleFieldChange('counterCode', typeof val === 'object' ? val?.target?.value : val)}
                    options={COUNTER_OPTIONS}
                    buttonClassName="!py-3 !rounded-[10px] !border-[#cbd5e1] !text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* 3. Tên cán bộ (officerName) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                    Tên cán bộ tiếp nhận * <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>(2 - 100 ký tự)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={formData.officerName}
                    onChange={(e) => handleFieldChange('officerName', e.target.value)}
                    onBlur={() => handleFieldBlur('officerName')}
                    placeholder="VD: Trần Thị Bình"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      fontSize: '14px',
                      borderRadius: '10px',
                      border: touched.officerName && fieldErrors.officerName ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                      outline: 'none',
                      backgroundColor: touched.officerName && fieldErrors.officerName ? '#fef2f2' : '#fff'
                    }}
                  />
                  {touched.officerName && fieldErrors.officerName && (
                    <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', margin: '4px 0 0', fontWeight: '600' }}>
                      ⚠️ {fieldErrors.officerName}
                    </p>
                  )}
                </div>

                {/* 4. Họ tên công dân (citizenName) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                    Họ tên công dân * <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>(2 - 100 ký tự)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={formData.citizenName}
                    onChange={(e) => handleFieldChange('citizenName', e.target.value)}
                    onBlur={() => handleFieldBlur('citizenName')}
                    placeholder="VD: Nguyễn Văn An"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      fontSize: '14px',
                      borderRadius: '10px',
                      border: touched.citizenName && fieldErrors.citizenName ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                      outline: 'none',
                      backgroundColor: touched.citizenName && fieldErrors.citizenName ? '#fef2f2' : '#fff'
                    }}
                  />
                  {touched.citizenName && fieldErrors.citizenName && (
                    <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', margin: '4px 0 0', fontWeight: '600' }}>
                      ⚠️ {fieldErrors.citizenName}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* 5. Ngày tiếp nhận (receptionDate) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                    Ngày tiếp nhận *
                  </label>
                  <VietnameseDatePicker
                    value={formData.receptionDate}
                    onChange={(isoVal) => setFormData({ ...formData, receptionDate: isoVal })}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                {/* 6. Khung giờ tiếp nhận (timeSlot) */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                    Khung giờ tiếp nhận *
                  </label>
                  <CustomSelect
                    value={formData.timeSlot}
                    onChange={(val) => handleFieldChange('timeSlot', typeof val === 'object' ? val?.target?.value : val)}
                    options={TIME_SLOT_OPTIONS.map((slot) => ({ value: slot, label: slot }))}
                    buttonClassName="!py-3 !rounded-[10px] !border-[#cbd5e1] !text-sm"
                  />
                </div>
              </div>

              {/* 7. Nội dung làm việc (workingContent) */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                    Nội dung tiếp dân / làm việc * <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>(5 - 2000 ký tự)</span>
                  </label>
                  <span style={{ fontSize: '11px', color: formData.workingContent.length > 1900 ? '#ef4444' : '#94a3b8' }}>
                    {formData.workingContent.length}/2000 ký tự
                  </span>
                </div>
                <textarea
                  rows={2}
                  maxLength={2000}
                  value={formData.workingContent}
                  onChange={(e) => handleFieldChange('workingContent', e.target.value)}
                  onBlur={() => handleFieldBlur('workingContent')}
                  placeholder="VD: Hướng dẫn thủ tục hành chính, sao y chứng thực..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    borderRadius: '10px',
                    border: touched.workingContent && fieldErrors.workingContent ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                    outline: 'none',
                    backgroundColor: touched.workingContent && fieldErrors.workingContent ? '#fef2f2' : '#fff',
                    resize: 'none'
                  }}
                />
                {touched.workingContent && fieldErrors.workingContent && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', margin: '4px 0 0', fontWeight: '600' }}>
                    ⚠️ {fieldErrors.workingContent}
                  </p>
                )}
              </div>

              {/* Nút chuyển tiếp */}
              <button
                type="submit"
                className="reception-kiosk-primary"
                style={{ width: '100%', padding: '16px', fontSize: '17px', borderRadius: '14px', marginTop: '4px' }}
              >
                Chuyển cho Người dân đánh giá <ChevronRight size={20} />
              </button>
            </form>
          </div>
        )}

        {/* BƯỚC 2: NGƯỜI DÂN CHỌN SỐ SAO VÀ Ý KIẾN */}
        {step === 2 && (
          <div className="reception-kiosk-panel animate-fade-in" style={{ maxWidth: '640px', width: '100%', overflow: 'hidden' }}>
            <p className="reception-kiosk-eyebrow">Đánh giá chất lượng phục vụ</p>
            <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>
              Buổi tiếp dân hôm nay của bạn thế nào?
            </h1>
            <p className="reception-kiosk-lead" style={{ margin: '0 auto 16px', fontSize: '15px' }}>
              Chạm vào số sao tương ứng với mức độ hài lòng của bạn.
            </p>

            {/* Thẻ tóm tắt thông tin ca tiếp */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px 12px',
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '16px',
              fontSize: '13px',
              color: '#334155',
              marginBottom: '20px',
              maxWidth: '100%',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              overflow: 'hidden'
            }}>
              <span style={{ maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>Mã: <strong>{formData.receptionCode}</strong></span>
              <span>•</span>
              <span style={{ maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>Công dân: <strong>{formData.citizenName}</strong></span>
              <span>•</span>
              <span style={{ maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>Cán bộ: <strong>{formData.officerName}</strong></span>
              <span>•</span>
              <span><strong>{COUNTER_OPTIONS.find(c => c.value === formData.counterCode)?.label || formData.counterCode}</strong></span>
            </div>

            {/* 8. Chọn số sao (score: 1-5) */}
            <RatingStars 
              value={overall} 
              onChange={(star) => {
                setOverall(star);
              }} 
            />

            <strong className="reception-kiosk-rating-label" style={{ fontSize: '17px', color: overall ? '#0f172a' : '#64748b', marginTop: '10px', display: 'block' }}>
              {overall ? `${overall}/5 Sao · ${ratingLabels[overall]}` : 'Chạm vào ngôi sao để đánh giá'}
            </strong>

            {/* 9. Nhận xét / Ý kiến của công dân (comment) */}
            <div style={{ marginTop: '20px', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Ý kiến đóng góp / Nhận xét của bạn (Không bắt buộc):
                </label>
                <span style={{ fontSize: '11px', color: citizenComment.length > 1900 ? '#ef4444' : '#94a3b8' }}>
                  {citizenComment.length}/2000 ký tự
                </span>
              </div>
              <textarea
                rows={2}
                maxLength={2000}
                value={citizenComment}
                onChange={(e) => setCitizenComment(e.target.value)}
                placeholder="VD: Cán bộ hướng dẫn rất tận tình, giải quyết hồ sơ nhanh chóng..."
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  fontSize: '14px',
                  borderRadius: '12px',
                  border: '1.5px solid #cbd5e1',
                  outline: 'none',
                  backgroundColor: '#fff',
                  resize: 'none'
                }}
              />
            </div>

            {/* Nút gửi đánh giá */}
            <div style={{ marginTop: '28px' }}>
              <button
                type="button"
                className="reception-kiosk-primary"
                disabled={overall === 0 || submitting}
                onClick={handleSendRating}
                style={{
                  minWidth: '240px',
                  padding: '15px 36px',
                  fontSize: '17px',
                  borderRadius: '14px'
                }}
              >
                {submitting ? 'Đang gửi...' : 'Gửi đánh giá'} <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}

      </section>
    </main>
  );
}
